class shopTheLook extends HTMLElement {
    constructor() {
      super();
      this.hotspots = this.querySelectorAll(".hotspot");
      this.carouselElement = this.querySelector("carousel-component");
      this.productBundleContainer = this.querySelector(".product__bundle__container");
  
      // Check if the carousel element exists
      if (!this.carouselElement) {
        console.warn("Carousel component not found within hotspot-carousel.");
        return;
      }
  
      // Create a single modal container
      this.createModalContainer();
  
      // Set up event listeners for hotspots
      this.setupHotspotListeners();
    }
  
    createModalContainer() {
      this.modalContainer = document.createElement('modal-container');
      this.modalViewer = document.createElement('modal-viewer');
      this.modalOverlay = document.createElement('modal-overlay');
      this.modalContent = document.createElement('modal-content');
  
      this.modalViewer.appendChild(this.modalOverlay);
      this.modalViewer.appendChild(this.modalContent);
      this.modalContainer.appendChild(this.modalViewer);
      document.body.appendChild(this.modalContainer);
  
      this.modalOverlay.addEventListener('click', () => {
        this.toggleModal(false);
      });
    }
  
    setupHotspotListeners() {
      this.hotspots.forEach((hotspot) => {
        hotspot.addEventListener("mouseenter", () => {
          const productIndex = hotspot.getAttribute("data-product-index");
  
          // Dispatch custom event to update the carousel
          this.carouselElement.dispatchEvent(
            new CustomEvent("updateSlider", {
              detail: { index: parseInt(productIndex) },
              bubbles: true, // Allow event to bubble up
              composed: true, // Allows event to cross the shadow DOM boundary
            })
          );
        });

        // Add click event for mobile to show modal
        hotspot.addEventListener("click", () => {
          if (window.innerWidth < 750) { // Check if the device is mobile
            this.showProductBundleModal();
          }
        });
      });
    }

    showProductBundleModal() {
      // Clone the product bundle container for the modal content
      const productBundleClone = this.productBundleContainer.cloneNode(true);
      productBundleClone.style.display = 'flex'; // Ensure it's visible in the modal
      productBundleClone.style.position= 'unset';

      this.modalContent.innerHTML = ''; // Clear previous content
      this.modalContent.appendChild(productBundleClone);
      this.toggleModal(true);
    }

    toggleModal(isActive) {
      document.body.classList.toggle('overflow-hidden', isActive);
      this.modalViewer.classList.toggle('active', isActive);
    }
  }
  
  customElements.define("shop-the-look", shopTheLook);   


  class ProductShopBundle extends HTMLElement {
    constructor() {
      super();
      this.addToCartButton = this.closest('.product__bundle__container').querySelector('#product-shop-bundle-button');
      this.productItems = this.querySelectorAll('[data-variant-id]');
      this.productBundle = {
        items: [],
        sections: ["cart-drawer", "cart-icon-bubble"],
        section_url: `${window.location.pathname}`,
      };
      this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      this.init();
    }

    init() {
      if (this.addToCartButton) {
        this.addToCartButton.addEventListener('click', this.handleAddCompleteSet.bind(this));
      }
      // Initialize max quantity messages
      this.updateMaxQuantityMessages();
    }

    async getCurrentCart() {
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        if (!response.ok) throw new Error('Failed to fetch cart');
        return await response.json();
      } catch (error) {
        console.error('Error fetching cart:', error);
        return null;
      }
    }

    async updateMaxQuantityMessages() {
      const currentCart = await this.getCurrentCart();
      if (!currentCart) return;

      // Create a map of variant IDs to their quantities in cart
      const cartQuantityMap = new Map(
        currentCart.items.map(item => [item.variant_id.toString(), item.quantity])
      );

      // Update messages for each product
      this.productItems.forEach(item => {
        const variantId = item.dataset.variantId;
        const currentQuantity = cartQuantityMap.get(variantId) || 0;
        const maxQuantity = parseInt(item.dataset.maxQuantity) || 999;
        const messageElement = item.querySelector('.max-quantity-message');

        if (messageElement) {
          const shouldShow = currentQuantity >= maxQuantity;
          messageElement.classList.toggle('tw-hidden', !shouldShow);

          // If message is shown, set timeout to hide it after 10 seconds
          if (shouldShow) {
            // Clear any existing timeout
            if (messageElement.hideTimeout) {
              clearTimeout(messageElement.hideTimeout);
            }
            
            // Set new timeout
            messageElement.hideTimeout = setTimeout(() => {
              messageElement.classList.add('tw-hidden');
            }, 10000); // 10 seconds
          }
        }
      });
    }

    async handleAddCompleteSet(event) {
      event.preventDefault();
      this.addToCartButton.classList.add('loading');
      this.addToCartButton.setAttribute("aria-disabled", true);
      this.addToCartButton.querySelector(".loading-overlay__spinner").classList.remove("hidden");

      try {
        // Get current cart state
        const currentCart = await this.getCurrentCart();
        if (!currentCart) throw new Error('Could not fetch cart state');

        // Create a map of variant IDs to their quantities in cart
        const cartQuantityMap = new Map(
          currentCart.items.map(item => [item.variant_id.toString(), item.quantity])
        );

        // Filter out unavailable products and those already at max quantity
        this.productBundle.items = Array.from(this.productItems)
          .filter(item => {
            const isAvailable = item.dataset.available === 'true';
            const variantId = item.dataset.variantId;
            const currentQuantity = cartQuantityMap.get(variantId) || 0;
            const maxQuantity = parseInt(item.dataset.maxQuantity) || 999;
            
            // Skip if product is unavailable or already at max quantity
            return isAvailable && currentQuantity < maxQuantity;
          })
          .map(item => ({
            id: item.dataset.variantId,
            quantity: 1
          }));

        // If no available products to add, show error
        if (this.productBundle.items.length === 0) {
          throw new Error('No available products to add to cart');
        }

        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.productBundle)
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const data = await response.json();
          cartDrawer.renderContents(data);

          // Update max quantity messages after adding to cart
          await this.updateMaxQuantityMessages();

          // Close the modal after rendering cart contents
          const shopTheLookElement = document.querySelector('shop-the-look');
          if (shopTheLookElement) {
            shopTheLookElement.toggleModal(false);
          }
        }

      } catch (error) {
        console.error('Error adding products to cart:', error);
      } finally {
        this.addToCartButton?.classList.remove("loading");
        if (this.cart && this.cart?.classList.contains("is-empty"))
          this.cart?.classList.remove("is-empty");
        if (!this.error) this.addToCartButton.removeAttribute("aria-disabled");
          this.addToCartButton.querySelector(".loading-overlay__spinner")?.classList.add("hidden");
      }
    }
  }
  
  customElements.define("product-shop-bundle", ProductShopBundle);